import React from "react";
import Link from "next/link";

/**
 * Button component that can be rendered as a button or a link
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant ('primary', 'secondary', 'outline', 'gradient')
 * @param {string} props.href - URL for link buttons (optional)
 * @param {boolean} props.fullWidth - Whether the button should take full width
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.size - Button size ('sm', 'md', 'lg')
 * @param {React.ReactNode} props.rightIcon - Icon to display on the right side
 * @param {React.ReactNode} props.leftIcon - Icon to display on the left side
 * @param {boolean} props.animated - Whether to add hover animation
 * @returns {React.ReactElement}
 */
const Button = ({
  variant = "primary",
  href,
  fullWidth = false,
  children,
  className = "",
  onClick,
  disabled = false,
  size = "md",
  rightIcon,
  leftIcon,
  animated = true,
  ...props
}) => {
  const baseClasses =
    "rounded-md font-medium transition-all duration-300 inline-flex items-center justify-center relative";

  // Size classes
  const sizeClasses = {
    sm: "px-4 py-1.5 text-sm",
    md: "px-6 py-2.5 text-base",
    lg: "px-8 py-3.5 text-lg",
  };

  const variantClasses = {
    primary:
      "bg-[#6111ce] text-white hover:shadow-lg relative overflow-hidden group",
    secondary: "border border-[#6111ce]/30 text-white hover:bg-white/5 group",
    outline: "border border-[#6111ce]/30 text-[#6111ce] hover:bg-white group",
    gradient:
      "bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white hover:shadow-lg hover:shadow-indigo-500/20 relative overflow-hidden group",
  };

  const widthClasses = fullWidth ? "w-full" : "";

  const disabledClasses = disabled ? "opacity-60 cursor-not-allowed" : "";

  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClasses} ${disabledClasses} ${className}`;

  const buttonContent = (
    <>
      {leftIcon && (
        <span className="relative z-10 mr-2 group-hover:scale-110 transition-transform">
          {leftIcon}
        </span>
      )}
      <span
        className={`relative z-10 ${
          variant === "outline"
            ? "group-hover:text-[#8339EA]"
            : "group-hover:text-white"
        } transition-colors`}
      >
        {children}
      </span>
      {rightIcon && (
        <span className="relative z-10 ml-2 group-hover:translate-x-1 transition-transform">
          {rightIcon}
        </span>
      )}
      {animated && variant === "primary" && (
        <span className="absolute inset-0 bg-[#511d9b] opacity-0 group-hover:opacity-100 transition-all duration-500"></span>
      )}
      {animated && variant === "outline" && (
        <span className="absolute inset-0 bg-[#8339EA] opacity-0 group-hover:opacity-10 transition-all duration-500"></span>
      )}
      {animated && variant === "gradient" && (
        <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-all duration-500"></span>
      )}
    </>
  );

  if (href) {
    return (
      <Link href={href} className={buttonClasses} {...props}>
        {buttonContent}
      </Link>
    );
  }

  return (
    <button
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
